import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AgreementForm from '../components/AgreementForm';
import './Agreement.css';
import { supabase } from '../supabaseClient';
import { useToast, useConfirm } from '../components/Toast';
import { useAuth } from '../hooks/useAuth';

// Role permissions
const ROLE_PERMISSIONS = {
  admin:       ['create', 'read', 'edit', 'delete', 'manage_employees', 'view_customers'],
  manager:     ['create', 'read', 'edit', 'view_customers'],
  sales_agent: ['create', 'read'],
  viewer:      ['read', 'view_customers'],
};

const hasPermission = (user, permission) => {
  if (!user) return false;
  const perms = ROLE_PERMISSIONS[user.role] || [];
  return perms.includes(permission);
};

const ROLE_LABELS = {
  admin: 'Admin',
  manager: 'Manager',
  sales_agent: 'Sales Agent',
  viewer: 'Viewer',
};

function Agreement() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, login, logout } = useAuth();
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [view, setView] = useState('dashboard');
  const [agreementType, setAgreementType] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [resumeDraft, setResumeDraft] = useState(null);
  const [activityLimit, setActivityLimit] = useState(30);
  const [clearingLog, setClearingLog] = useState(false);
  const [draftLogDraft, setDraftLogDraft] = useState(null);   // Task 1: per-draft log
  const [draftActivities, setDraftActivities] = useState([]); // Task 1
  const [draftLogLoading, setDraftLogLoading] = useState(false);
  const { showToast, ToastComponent } = useToast();
  const { showConfirm, ConfirmComponent } = useConfirm();

  const fetchDrafts = useCallback(async () => {
    setLoadingDrafts(true);
    const { data } = await supabase
      .from('agreement_drafts')
      .select('*')
      .order('updated_at', { ascending: false });
    if (data) setDrafts(data);
    setLoadingDrafts(false);
  }, []);

  const fetchActivity = useCallback(async (limit) => {
    const { data } = await supabase
      .from('agreement_activity')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (data) setActivities(data);
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchDrafts();
      fetchActivity(activityLimit);

      const interval = setInterval(() => {
        fetchActivity(activityLimit);
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [currentUser, activityLimit, fetchDrafts, fetchActivity]);

  // Sidebar "Saved Drafts" / "Drafts" links navigate to /agreement#anchor —
  // if the user was mid-form, snap back to the dashboard so the target exists.
  useEffect(() => {
    if (location.hash === '#saved-drafts' || location.hash === '#auto-drafts') {
      setView('dashboard');
      setAgreementType(null);
      setResumeDraft(null);
    }
  }, [location.hash]);

  // Smooth-scroll to the requested section once the dashboard is showing.
  useEffect(() => {
    if (view !== 'dashboard' || !location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [view, location.hash]);

  const handleLogin = async () => {
    setLoginLoading(true);
    setLoginError('');

    const result = await login(loginData.username, loginData.password);
    if (!result.success) {
      setLoginError('Invalid username or password.');
    }

    setLoginLoading(false);
  };

  const handleLogout = () => {
    logout();
    setLoginData({ username: '', password: '' });
    setView('dashboard');
    setAgreementType(null);
    setResumeDraft(null);
    // Send them out to the main gateway instead of this module's own login form.
    navigate('/hub');
  };

  const handleActivityLimitChange = (newLimit) => {
    setActivityLimit(newLimit);
  };

  const clearActivityLog = () => {
    showConfirm({
      title: 'Clear Activity Log',
      message: 'Are you sure you want to clear the entire activity log? This cannot be undone.',
      confirmLabel: 'Yes, Clear',
      danger: true,
      onConfirm: async () => {
        setClearingLog(true);
        await supabase.from('agreement_activity').delete().neq('id', '00000000-0000-0000-0000-000000000000');
        setActivities([]);
        setClearingLog(false);
        showToast('Activity log cleared successfully.', 'success');
      },
    });
  };

  const logActivity = async (action, ref, details) => {
    await supabase.from('agreement_activity').insert([{
      agreement_ref: ref || null,
      action,
      performed_by: currentUser.name,
      details: details || null,
    }]);
    fetchActivity(activityLimit);
  };

  const saveDraft = async (formData, agreementType, draftName) => {
    const name = draftName || `Draft - ${formData.buyers[0]?.buyerCompany || 'Unnamed'} - ${new Date().toLocaleDateString()}`;
    const { data, error } = await supabase.from('agreement_drafts').insert([{
      draft_name: name,
      form_data: formData,
      agreement_type: agreementType,
      is_auto: false,
      created_by: currentUser.name,
      updated_by: currentUser.name,
    }]).select().single();
    if (!error) {
      await logActivity('Draft Saved', formData.refNumber, `"${name}" saved by ${currentUser.name}`);
      fetchDrafts();
      showToast(`Draft "${name}" saved successfully!`, 'success');
      return { id: data.id };
    }
    return { id: null };
  };

  const updateDraft = async (draftId, formData, agreementType) => {
    const { error } = await supabase.from('agreement_drafts')
      .update({
        form_data: formData,
        agreement_type: agreementType,
        updated_by: currentUser.name,
        updated_at: new Date().toISOString()
      })
      .eq('id', draftId);
    if (error) {
      showToast('Failed to update draft. Please try again.', 'error');
      return;
    }
    await logActivity('Draft Updated', formData.refNumber, `Draft updated by ${currentUser.name}`);
    fetchDrafts();
  };

  // Promote a silently auto-saved draft into a named "Saved Draft" —
  // updates the SAME row instead of creating a duplicate.
  const promoteDraft = async (draftId, formData, agreementType, draftName) => {
    const name = draftName || `Draft - ${formData.buyers[0]?.buyerCompany || 'Unnamed'} - ${new Date().toLocaleDateString()}`;
    const { error } = await supabase.from('agreement_drafts')
      .update({
        draft_name: name,
        form_data: formData,
        agreement_type: agreementType,
        is_auto: false,
        updated_by: currentUser.name,
        updated_at: new Date().toISOString(),
      })
      .eq('id', draftId);
    if (!error) {
      await logActivity('Draft Saved', formData.refNumber, `"${name}" saved by ${currentUser.name}`);
      fetchDrafts();
      showToast(`Draft "${name}" saved successfully!`, 'success');
    }
  };

  // Silent background auto-save — runs while the user works without clicking
  // "Save as Draft". Creates a hidden "is_auto" row on first call, then keeps
  // updating that same row. Never touches the activity log (would spam it).
  const autoSaveDraft = async (existingId, formData, agreementType) => {
    if (existingId) {
      const { error } = await supabase.from('agreement_drafts')
        .update({
          form_data: formData,
          agreement_type: agreementType,
          updated_by: currentUser.name,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existingId);
      if (!error) fetchDrafts();
      return { id: existingId, created: false };
    }
    const name = `Auto-draft - ${formData.buyers?.[0]?.buyerCompany || 'Unnamed'} - ${new Date().toLocaleDateString()}`;
    const { data, error } = await supabase.from('agreement_drafts').insert([{
      draft_name: name,
      form_data: formData,
      agreement_type: agreementType,
      is_auto: true,
      created_by: currentUser.name,
      updated_by: currentUser.name,
    }]).select().single();
    if (error) return { id: null, created: false };
    fetchDrafts();
    return { id: data.id, created: true };
  };

  const deleteDraft = (draftId, draftName) => {
    showConfirm({
      title: 'Delete Draft',
      message: `Are you sure you want to delete "${draftName}"?`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        await supabase.from('agreement_drafts').delete().eq('id', draftId);
        await logActivity('Draft Deleted', null, `"${draftName}" deleted by ${currentUser.name}`);
        fetchDrafts();
        showToast(`Draft "${draftName}" deleted.`, 'success');
      },
    });
  };

  const resumeDraftHandler = (draft) => {
    if (!hasPermission(currentUser, 'read')) return;
    setResumeDraft(draft);
    setAgreementType(draft.agreement_type);
    setView('draft');
    logActivity('Draft Resumed', draft.form_data?.refNumber, `"${draft.draft_name}" resumed by ${currentUser.name}`);
  };

  // Task 1: open per-draft activity log
  const openDraftLog = async (draft) => {
    setDraftLogDraft(draft);
    setDraftLogLoading(true);
    const ref = draft.form_data?.refNumber;
    if (ref) {
      const { data } = await supabase
        .from('agreement_activity')
        .select('*')
        .eq('agreement_ref', ref)
        .order('created_at', { ascending: false });
      setDraftActivities(data || []);
    } else {
      setDraftActivities([]);
    }
    setDraftLogLoading(false);
  };

  // Task 4: mark draft as completed
  const completeDraft = (draft) => {
    showConfirm({
      title: 'Complete Agreement',
      message: `Mark "${draft.draft_name}" as completed? It will be moved out of active drafts.`,
      confirmLabel: 'Yes, Complete',
      danger: false,
      onConfirm: async () => {
        await supabase.from('agreement_drafts')
          .update({ status: 'completed', updated_at: new Date().toISOString() })
          .eq('id', draft.id);
        await logActivity('Agreement Completed', draft.form_data?.refNumber, `"${draft.draft_name}" marked complete by ${currentUser.name}`);
        fetchDrafts();
        showToast(`"${draft.draft_name}" marked as completed.`, 'success');
      },
    });
  };

  const formatTimeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const actionIcon = (action) => {
    if (action.includes('Saved')) return '💾';
    if (action.includes('Updated')) return '✏️';
    if (action.includes('Deleted')) return '🗑️';
    if (action.includes('Resumed')) return '▶️';
    if (action.includes('Completed')) return '✅';
    if (action.includes('Shared')) return '🔗';
    if (action.includes('Signed')) return '✍️';
    return '📋';
  };

  const roleLabel = currentUser ? (ROLE_LABELS[currentUser.role] || currentUser.role) : '';
  const activeDrafts = drafts.filter(d => d.status !== 'completed');
  const savedDraftsList = activeDrafts.filter(d => !d.is_auto);
  const autoDraftsList = activeDrafts.filter(d => d.is_auto);

  // ── LOGIN ──
  if (!currentUser) {
    return (
      <div className="agreement-login">
        <div className="login-card">
          <div className="login-logo">📄</div>
          <p className="login-brand">AL MUSHTARAKA TRADING CO.</p>
          <h2>AGREEMENT MODULE</h2>
          <p>Sign in to access the agreement system</p>
          <div className="login-field">
            <label>Username</label>
            <input
              type="text"
              placeholder="Enter username"
              value={loginData.username}
              onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            />
          </div>
          <div className="login-field">
            <label>Password</label>
            <input
              type="password"
              placeholder="Enter password"
              value={loginData.password}
              onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            />
          </div>
          {loginError && <p className="login-error">⚠️ {loginError}</p>}
          <button className="btn-login" onClick={handleLogin} disabled={loginLoading}>
            {loginLoading ? 'Signing in...' : 'Sign In →'}
          </button>
        </div>
      </div>
    );
  }

  // ── VIEWER — read only, no form access ──
  if (currentUser.role === 'viewer') {
    return (
      <div className="agreement-page">
        <div className="dashboard-header">
          <div>
            <h1>Agreement Dashboard</h1>
            <p className="dashboard-subtitle">
              Welcome, <strong>{currentUser.name}</strong>
              <span className="role-badge viewer">{roleLabel}</span>
            </p>
          </div>
          <div className="dashboard-header-actions">
            <button className="btn-logout-agreement" onClick={handleLogout}>Logout</button>
          </div>
        </div>

        <div className="viewer-notice">
          👁️ You have <strong>read-only</strong> access. Contact your admin to request additional permissions.
        </div>

        <div className="dashboard-content">
          <div className="dashboard-grid">
            <div className="dashboard-section" id="saved-drafts">
              <div className="section-header">
                <h2>💾 Saved Drafts</h2>
                <span className="section-count">{savedDraftsList.length}</span>
              </div>
              {loadingDrafts ? <p className="loading-text">Loading...</p> :
                savedDraftsList.length === 0 ? <div className="empty-state"><p>No saved drafts yet.</p></div> : (
                  <div className="drafts-list">
                    {savedDraftsList.map((draft) => (
                      <div className="draft-card" key={draft.id}>
                        <div className="draft-card-left">
                          <div className="draft-type-icon">{draft.agreement_type === 'sale' ? '📄' : '📋'}</div>
                          <div>
                            <p className="draft-name">{draft.draft_name}</p>
                            <div className="draft-meta">
                              <span className={`draft-type-badge ${draft.agreement_type}`}>{draft.agreement_type === 'sale' ? 'Sale' : 'Lease'}</span>
                              <span>by {draft.created_by}</span>
                              <span>{formatTimeAgo(draft.updated_at || draft.created_at)}</span>
                            </div>
                          </div>
                        </div>
                        {/* Viewer can only view, not resume */}
                        <span className="viewer-draft-tag">View Only</span>
                      </div>
                    ))}
                  </div>
                )}
            </div>

            <div className="dashboard-section" id="auto-drafts">
              <div className="section-header">
                <h2>📝 Drafts</h2>
                <span className="section-count">{autoDraftsList.length}</span>
              </div>
              {loadingDrafts ? <p className="loading-text">Loading...</p> :
                autoDraftsList.length === 0 ? <div className="empty-state"><p>No in-progress drafts.</p></div> : (
                  <div className="drafts-list">
                    {autoDraftsList.map((draft) => (
                      <div className="draft-card" key={draft.id}>
                        <div className="draft-card-left">
                          <div className="draft-type-icon">{draft.agreement_type === 'sale' ? '📄' : '📋'}</div>
                          <div>
                            <p className="draft-name">{draft.draft_name}</p>
                            <div className="draft-meta">
                              <span className={`draft-type-badge ${draft.agreement_type}`}>{draft.agreement_type === 'sale' ? 'Sale' : 'Lease'}</span>
                              <span>by {draft.created_by}</span>
                              <span>{formatTimeAgo(draft.updated_at || draft.created_at)}</span>
                            </div>
                          </div>
                        </div>
                        {/* Viewer can only view, not resume */}
                        <span className="viewer-draft-tag">View Only</span>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          </div>

          <div className="dashboard-section dashboard-section-full">
            <div className="section-header">
              <h2>📋 Activity Log</h2>
              <select
                className="activity-limit-select"
                value={activityLimit}
                onChange={(e) => handleActivityLimitChange(Number(e.target.value))}
              >
                <option value={30}>Last 30</option>
                <option value={60}>Last 60</option>
                <option value={90}>Last 90</option>
                <option value={150}>Last 150</option>
              </select>
            </div>
            {activities.length === 0 ? <div className="empty-state"><p>No activity yet.</p></div> : (
              <div className="activity-list">
                {activities.map((a) => (
                  <div className="activity-item" key={a.id}>
                    <div className="activity-icon">{actionIcon(a.action)}</div>
                    <div className="activity-content">
                      <p className="activity-action">{a.action}</p>
                      {a.details && <p className="activity-details">{a.details}</p>}
                      <p className="activity-meta">
                        <strong>{a.performed_by}</strong>
                        {a.agreement_ref && <span> · {a.agreement_ref}</span>}
                        <span> · {formatTimeAgo(a.created_at)}</span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── AGREEMENT TYPE SELECTION ──
  if ((view === 'new' || view === 'draft') && !agreementType) {
    return (
      <div className="agreement-page">
        <div className="agreement-topbar">
          <button className="btn-back" onClick={() => { setView('dashboard'); setResumeDraft(null); }}>← Dashboard</button>
          <div className="topbar-user">
            <span>👤 {currentUser.name}</span>
            <span className={`role-badge ${currentUser.role}`}>{roleLabel}</span>
            <button className="btn-topbar-logout" onClick={handleLogout}>Logout</button>
          </div>
        </div>
        <div className="type-selection">
          <h1>Create Agreement</h1>
          <p>Please select the type of agreement you want to generate</p>
          <div className="type-cards">
            <div className="type-card" onClick={() => setAgreementType('sale')}>
              <div className="type-icon">📄</div>
              <h2>Sale Agreement</h2>
              <p>Standard sales agreement for direct purchase of goods.</p>
              <button className="btn-select">Select</button>
            </div>
            <div className="type-card" onClick={() => setAgreementType('lease')}>
              <div className="type-icon">📋</div>
              <h2>Lease Cum Sale Agreement</h2>
              <p>Lease agreement with installment payment plan and guarantor.</p>
              <button className="btn-select">Select</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── AGREEMENT FORM ──
  if ((view === 'new' || view === 'draft') && agreementType) {
    return (
      <div className="agreement-page">
        <div className="agreement-topbar">
          <button className="btn-back" onClick={() => { setView('dashboard'); setAgreementType(null); setResumeDraft(null); }}>← Dashboard</button>
          <div className="topbar-user">
            <span>👤 {currentUser.name}</span>
            <span className={`role-badge ${currentUser.role}`}>{roleLabel}</span>
            <button className="btn-topbar-logout" onClick={handleLogout}>Logout</button>
          </div>
        </div>
        <AgreementForm
          agreementType={agreementType}
          currentUser={currentUser}
          initialData={resumeDraft?.form_data || null}
          draftId={resumeDraft?.id || null}
          isAutoDraft={resumeDraft?.is_auto || false}
          onSaveDraft={saveDraft}
          onUpdateDraft={updateDraft}
          onPromoteDraft={promoteDraft}
          onAutoSave={autoSaveDraft}
          onLogActivity={logActivity}
          canEdit={hasPermission(currentUser, 'edit')}
          canCreate={hasPermission(currentUser, 'create')}
        />
      </div>
    );
  }

  // ── DASHBOARD ──
  return (
    <div className="agreement-page">
      {ToastComponent}
      {ConfirmComponent}
      <div className="dashboard-header">
        <div>
          <h1>Agreement Dashboard</h1>
          <p className="dashboard-subtitle">
            Welcome back, <strong>{currentUser.name}</strong>
            <span className={`role-badge ${currentUser.role}`}>{roleLabel}</span>
          </p>
        </div>
        <div className="dashboard-header-actions">
          {hasPermission(currentUser, 'create') && (
            <button className="btn-new-agreement" onClick={() => { setResumeDraft(null); setAgreementType(null); setView('new'); }}>
              + New Agreement
            </button>
          )}
          <button className="btn-logout-agreement" onClick={handleLogout}>Logout</button>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="dashboard-grid">

          {/* Saved Drafts — explicitly saved by clicking "Save as Draft" */}
          <div className="dashboard-section" id="saved-drafts">
            <div className="section-header">
              <h2>💾 Saved Drafts</h2>
              <span className="section-count">{savedDraftsList.length}</span>
            </div>
            {loadingDrafts ? (
              <p className="loading-text">Loading...</p>
            ) : savedDraftsList.length === 0 ? (
              <div className="empty-state"><p>No saved drafts. Click "Save as Draft" while editing to keep one here.</p></div>
            ) : (
              <div className="drafts-list">
                {savedDraftsList.map((draft) => (
                  <div className="draft-card" key={draft.id}>
                    <div className="draft-card-left">
                      <div className="draft-type-icon">{draft.agreement_type === 'sale' ? '📄' : '📋'}</div>
                      <div className="draft-info">
                        <p className="draft-name">{draft.draft_name}</p>
                        <div className="draft-meta">
                          <span className={`draft-type-badge ${draft.agreement_type}`}>
                            {draft.agreement_type === 'sale' ? 'Sale' : 'Lease'}
                          </span>
                          <span>by {draft.created_by}</span>
                          <span>{formatTimeAgo(draft.updated_at || draft.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="draft-actions">
                      <button className="btn-draft-log" onClick={() => openDraftLog(draft)} title="View activity log">📋</button>
                      {hasPermission(currentUser, 'read') && (
                        <button className="btn-resume" onClick={() => resumeDraftHandler(draft)}>▶ Resume</button>
                      )}
                      {hasPermission(currentUser, 'edit') && (
                        <button className="btn-complete-draft" onClick={() => completeDraft(draft)} title="Mark as completed">✅</button>
                      )}
                      {hasPermission(currentUser, 'delete') && (
                        <button className="btn-delete-draft" onClick={() => deleteDraft(draft.id, draft.draft_name)}>🗑️</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Drafts — auto-saved while editing, never explicitly saved */}
          <div className="dashboard-section" id="auto-drafts">
            <div className="section-header">
              <h2>📝 Drafts</h2>
              <span className="section-count">{autoDraftsList.length}</span>
            </div>
            {loadingDrafts ? (
              <p className="loading-text">Loading...</p>
            ) : autoDraftsList.length === 0 ? (
              <div className="empty-state"><p>No in-progress drafts. Anything left unsaved while editing appears here automatically.</p></div>
            ) : (
              <div className="drafts-list">
                {autoDraftsList.map((draft) => (
                  <div className="draft-card" key={draft.id}>
                    <div className="draft-card-left">
                      <div className="draft-type-icon">{draft.agreement_type === 'sale' ? '📄' : '📋'}</div>
                      <div className="draft-info">
                        <p className="draft-name">{draft.draft_name}</p>
                        <div className="draft-meta">
                          <span className={`draft-type-badge ${draft.agreement_type}`}>
                            {draft.agreement_type === 'sale' ? 'Sale' : 'Lease'}
                          </span>
                          <span>by {draft.created_by}</span>
                          <span>{formatTimeAgo(draft.updated_at || draft.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="draft-actions">
                      <button className="btn-draft-log" onClick={() => openDraftLog(draft)} title="View activity log">📋</button>
                      {hasPermission(currentUser, 'read') && (
                        <button className="btn-resume" onClick={() => resumeDraftHandler(draft)}>▶ Resume</button>
                      )}
                      {hasPermission(currentUser, 'edit') && (
                        <button className="btn-complete-draft" onClick={() => completeDraft(draft)} title="Mark as completed">✅</button>
                      )}
                      {hasPermission(currentUser, 'delete') && (
                        <button className="btn-delete-draft" onClick={() => deleteDraft(draft.id, draft.draft_name)}>🗑️</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

        {/* Activity Log — full width below */}
        <div className="dashboard-section dashboard-section-full">
          <div className="section-header">
            <h2>📋 Activity Log</h2>
            <div className="activity-header-controls">
              <select
                className="activity-limit-select"
                value={activityLimit}
                onChange={(e) => handleActivityLimitChange(Number(e.target.value))}
                title="Number of entries to show"
              >
                <option value={30}>Last 30</option>
                <option value={60}>Last 60</option>
                <option value={90}>Last 90</option>
                <option value={150}>Last 150</option>
              </select>
              {hasPermission(currentUser, 'delete') && (
                <button
                  className="btn-clear-log"
                  onClick={clearActivityLog}
                  disabled={clearingLog || activities.length === 0}
                  title="Clear all activity logs"
                >
                  {clearingLog ? '⏳' : '🗑️ Clear Log'}
                </button>
              )}
            </div>
          </div>
          {activities.length === 0 ? (
            <div className="empty-state"><p>No activity recorded yet.</p></div>
          ) : (
            <div className="activity-list">
              {activities.map((a) => (
                <div className="activity-item" key={a.id}>
                  <div className="activity-icon">{actionIcon(a.action)}</div>
                  <div className="activity-content">
                    <p className="activity-action">{a.action}</p>
                    {a.details && <p className="activity-details">{a.details}</p>}
                    <p className="activity-meta">
                      <strong>{a.performed_by}</strong>
                      {a.agreement_ref && <span> · {a.agreement_ref}</span>}
                      <span> · {formatTimeAgo(a.created_at)}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Task 1: Per-draft activity log drawer */}
      {draftLogDraft && (
        <div className="draft-log-overlay" onClick={() => setDraftLogDraft(null)}>
          <div className="draft-log-panel" onClick={e => e.stopPropagation()}>
            <div className="draft-log-header">
              <div>
                <p className="draft-log-label">ACTIVITY LOG</p>
                <h3 className="draft-log-title">{draftLogDraft.draft_name}</h3>
                {draftLogDraft.form_data?.refNumber && (
                  <p className="draft-log-ref">Ref: {draftLogDraft.form_data.refNumber}</p>
                )}
              </div>
              <button className="draft-log-close" onClick={() => setDraftLogDraft(null)}>✕</button>
            </div>
            <div className="draft-log-body">
              {draftLogLoading ? (
                <p className="loading-text">Loading...</p>
              ) : draftActivities.length === 0 ? (
                <div className="empty-state"><p>No activity recorded for this agreement yet.</p></div>
              ) : (
                draftActivities.map((a) => (
                  <div className="activity-item" key={a.id}>
                    <div className="activity-icon">{actionIcon(a.action)}</div>
                    <div className="activity-content">
                      <p className="activity-action">{a.action}</p>
                      {a.details && <p className="activity-details">{a.details}</p>}
                      <p className="activity-meta">
                        <strong>{a.performed_by}</strong>
                        <span> · {formatTimeAgo(a.created_at)}</span>
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Agreement;